import { WIDGET_COLOR_PARAMS } from 'src/app/services/platform/widget.service';

/**
 * The widget's URL parameters as the documentation page edits them: one entry per parameter,
 * in the order they are written into the URL. The widget side of every entry lives in
 * WidgetService (parsing, ranges) — keep both in step.
 */

export interface DocOption {
  value: string;
  label: string;
}

export type DocControl =
  /** 0 / 1, shown as a three-way "Not set | Off | On" segmented control. */
  | { kind: 'toggle' }
  /** A fixed set of values: segmented when short, a select when `select` is set. */
  | { kind: 'choice'; options: DocOption[]; select?: boolean }
  /** Slider + number box; `start` is where the slider sits while the parameter is not set. */
  | { kind: 'range'; min: number; max: number; step: number; start: number; unit?: string }
  /** Free number (coordinates, ids); clamped to min/max when given. */
  | { kind: 'number'; min?: number; max?: number; step?: number; placeholder: string }
  | { kind: 'text'; placeholder: string }
  /** The charger type chips of `chargers`. */
  | { kind: 'chargers' }
  /** The operator id list of `favoriteoperators`: a picker plus removable chips. */
  | { kind: 'operators' }
  /** Edited by the color builder of the Colors section. */
  | { kind: 'color' };

export interface DocParam {
  name: string;
  /** Allowed values as documented, rendered as code. */
  values: string;
  /** What the widget does without the parameter. */
  fallback: string;
  description: string;
  control: DocControl;
}

export interface DocParamGroup {
  id: string;
  params: DocParam[];
}

const TOGGLE: DocControl = { kind: 'toggle' };
const UNIT: DocControl = { kind: 'choice', options: [{ value: 'metric', label: 'Metric' }, { value: 'imperial', label: 'Imperial' }] };

export const CHARGER_TYPES: DocOption[] = [
  { value: 'ccs', label: 'CCS' },
  { value: 'chademo', label: 'CHAdeMO' },
  { value: 'schuko', label: 'Schuko' },
  { value: 'tesla', label: 'Tesla / Supercharger' },
  { value: 'type1', label: 'Type 1' },
  { value: 'type2', label: 'Type 2' },
];

export const LANGUAGES: DocOption[] = [
  { value: 'en', label: 'English' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'ca', label: 'Català' },
  { value: 'cs', label: 'Čeština' },
  { value: 'cy', label: 'Cymraeg' },
  { value: 'da', label: 'Dansk' },
  { value: 'de', label: 'Deutsch' },
  { value: 'et', label: 'Eesti' },
  { value: 'es', label: 'Español' },
  { value: 'eu', label: 'Euskara' },
  { value: 'fr', label: 'Français' },
  { value: 'ga', label: 'Gaeilge' },
  { value: 'gl', label: 'Galego' },
  { value: 'hr', label: 'Hrvatski' },
  { value: 'is', label: 'Íslenska' },
  { value: 'it', label: 'Italiano' },
  { value: 'lv', label: 'Latviešu' },
  { value: 'lb', label: 'Lëtzebuergesch' },
  { value: 'lt', label: 'Lietuvių' },
  { value: 'hu', label: 'Magyar' },
  { value: 'mt', label: 'Malti' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'no', label: 'Norsk' },
  { value: 'pl', label: 'Polski' },
  { value: 'br', label: 'Português (Brasil)' },
  { value: 'pt', label: 'Português (Portugal)' },
  { value: 'ro', label: 'Română' },
  { value: 'sq', label: 'Shqip' },
  { value: 'sk', label: 'Slovenčina' },
  { value: 'sl', label: 'Slovenščina' },
  { value: 'fi', label: 'Suomi' },
  { value: 'se', label: 'Svenska' },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'tr', label: 'Türkçe' },
  { value: 'el', label: 'Ελληνικά' },
  { value: 'bg', label: 'Български' },
  { value: 'mk', label: 'Македонски' },
  { value: 'ru', label: 'Русский' },
  { value: 'sr', label: 'Српски' },
  { value: 'uk', label: 'Українська' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'ja', label: '日本語' },
  { value: 'zh', label: '中文（简体）' },
];

export const ACCESS_PARAMS: DocParam[] = [
  {
    name: 'api_key', values: 'the key issued to you', fallback: 'error page',
    description: 'Required. A missing or rejected key replaces the widget with an error page; no map, resolver or calculation service is started.',
    control: { kind: 'text', placeholder: 'Paste your API key' },
  },
];

export const UI_SWITCH_PARAMS: DocParam[] = [
  {
    name: 'carselector', values: '0 / 1', fallback: '1',
    description: '0 locks the vehicle: the vehicle header is not clickable and the vehicle options dialog cannot be opened.',
    control: TOGGLE,
  },
  {
    name: 'batteryslider', values: '0 / 1', fallback: '1',
    description: '0 hides the battery level slider; the battery then comes from the battery parameter or the native bridge.',
    control: TOGGLE,
  },
  {
    name: 'settings', values: '0 / 1', fallback: '1',
    description: '0 hides the settings icon, so the user cannot change units, skin, routing or voice settings.',
    control: TOGGLE,
  },
  {
    name: 'stationlists', values: '0 / 1', fallback: '1',
    description: '0 hides the favorite and disabled charging station lists: no buttons in the charging options, no Normal / Favorite / Disabled selector in the charger popup. Lists stored by an earlier session are not applied.',
    control: TOGGLE,
  },
  {
    name: 'operatorlists', values: '0 / 1', fallback: '1',
    description: '0 hides the preferred and avoided charging operator lists in the charging options. Lists stored by an earlier session are not applied; favoriteoperators still is.',
    control: TOGGLE,
  },
];

export const VEHICLE_PARAMS: DocParam[] = [
  {
    name: 'carid', values: 'vehicle id', fallback: 'stored / default car',
    description: 'Selects the vehicle. An unknown id falls back to the stored selection, then to the default car.',
    control: { kind: 'number', min: 1, step: 1, placeholder: 'Vehicle id' },
  },
  { name: 'battery', values: '1 – 100', fallback: 'stored', description: 'State of charge.', control: { kind: 'range', min: 1, max: 100, step: 1, start: 80, unit: '%' } },
  { name: 'weight', values: '1 – 5', fallback: 'stored', description: 'Number of passengers.', control: { kind: 'range', min: 1, max: 5, step: 1, start: 1 } },
  { name: 'tirepressure', values: '1.5 – 2.6', fallback: 'stored', description: 'Tire pressure.', control: { kind: 'range', min: 1.5, max: 2.6, step: 0.1, start: 2.3, unit: 'bar' } },
  { name: 'userbehavior', values: '0 – 100', fallback: 'stored', description: 'Driving style: 0 efficiency focused … 100 performance oriented.', control: { kind: 'range', min: 0, max: 100, step: 5, start: 50 } },
  { name: 'speedlimiter', values: '80 – 130', fallback: 'stored', description: 'Speed limiter.', control: { kind: 'range', min: 80, max: 130, step: 5, start: 130, unit: 'km/h' } },
  { name: 'minbattery', values: '10 – 75', fallback: 'stored', description: 'Minimum battery at the destination.', control: { kind: 'range', min: 10, max: 75, step: 1, start: 15, unit: '%' } },
  { name: 'reserverange', values: '20 – 50', fallback: 'stored', description: 'Battery reserve in range mode.', control: { kind: 'range', min: 20, max: 50, step: 1, start: 20, unit: '%' } },
  { name: 'reserveroute', values: '10 – 30', fallback: 'stored', description: 'Battery reserve in route mode.', control: { kind: 'range', min: 10, max: 30, step: 1, start: 10, unit: '%' } },
  { name: 'soh', values: '50 – 110', fallback: 'stored', description: 'Battery state of health.', control: { kind: 'range', min: 50, max: 110, step: 1, start: 100, unit: '%' } },
  { name: 'fastchargelimit', values: '75 – 90', fallback: 'stored', description: 'Fast charge limit.', control: { kind: 'range', min: 75, max: 90, step: 1, start: 80, unit: '%' } },
  {
    name: 'chargers', values: 'name list', fallback: "the car's own connectors",
    description: 'Charger types the car can use. Unrecognised names are ignored.',
    control: { kind: 'chargers' },
  },
  {
    name: 'favoriteoperators', values: 'operator id list', fallback: 'stored',
    description: 'Charging operators preselected as preferred; routing favours their stations. Replaces the stored list, takes these operators off the avoided list, and also applies with operatorlists=0. Not written back to storage.',
    control: { kind: 'operators' },
  },
];

export const SETTINGS_PARAMS: DocParam[] = [
  { name: 'lang', values: 'bg, br, ca, cs, cy, da, de, el, en, es, et, eu, fi, fr, ga, gl, hi, hr, hu, id, is, it, ja, lb, lt, lv, mk, mt, nl, no, pl, pt, ro, ru, se, sk, sl, sq, sr, tr, uk, vi, zh', fallback: 'stored / browser', description: 'Interface language. Not written back to storage.', control: { kind: 'choice', options: LANGUAGES, select: true } },
  {
    name: 'skin', values: 'light / dark', fallback: 'light',
    description: 'Map skin and UI theme. Always light without the parameter; switching it inside the widget lasts for the session only.',
    control: { kind: 'choice', options: [{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }] },
  },
  { name: 'pois', values: '0 / 1', fallback: 'stored', description: 'Points of interest on the map.', control: TOGGLE },
  { name: 'unit', values: 'metric / imperial', fallback: 'stored / by country', description: 'Sets distance, temperature, speed and pressure units at once.', control: UNIT },
  { name: 'distance', values: 'metric / imperial', fallback: 'unit', description: 'Overrides just the distance unit.', control: UNIT },
  { name: 'temperature', values: 'metric / imperial', fallback: 'unit', description: 'Overrides just the temperature unit.', control: UNIT },
  { name: 'speed', values: 'metric / imperial', fallback: 'unit', description: 'Overrides just the speed unit.', control: UNIT },
  { name: 'pressure', values: 'metric / imperial', fallback: 'unit', description: 'Overrides just the pressure unit.', control: UNIT },
  { name: 'tollroads', values: '0 / 1', fallback: 'stored', description: 'Allow toll roads in routing.', control: TOGGLE },
  { name: 'ferries', values: '0 / 1', fallback: 'stored', description: 'Allow ferries in routing.', control: TOGGLE },
  { name: 'highways', values: '0 / 1', fallback: 'stored', description: 'Allow highways in routing.', control: TOGGLE },
  { name: 'mute', values: '0 / 1', fallback: 'stored', description: 'Mute voice guidance.', control: TOGGLE },
];

export const COLOR_PARAMS: DocParam[] = [
  ...WIDGET_COLOR_PARAMS.map((param): DocParam => ({
    name: param.name, values: 'hex without #', fallback: 'skin palette', description: param.description, control: { kind: 'color' },
  })),
  {
    name: 'maptint', values: '0 – 100', fallback: '25 with colors, else 0',
    description: 'How far the map follows the palette.',
    control: { kind: 'range', min: 0, max: 100, step: 5, start: 25, unit: '%' },
  },
];

export const MAP_PARAMS: DocParam[] = [
  {
    name: 'mode', values: 'range / route', fallback: 'stored',
    description: 'Initial display mode: range polygon or route planning.',
    control: { kind: 'choice', options: [{ value: 'range', label: 'Range' }, { value: 'route', label: 'Route' }] },
  },
  { name: 'lat', values: '-90 – 90', fallback: 'device position', description: 'Latitude of the start point.', control: { kind: 'number', min: -90, max: 90, step: 0.0001, placeholder: 'e.g. 47.4979' } },
  { name: 'lon', values: '-180 – 180', fallback: 'device position', description: 'Longitude of the start point (alias lng).', control: { kind: 'number', min: -180, max: 180, step: 0.0001, placeholder: 'e.g. 19.0402' } },
  { name: 'zoom', values: '1 – 20', fallback: '10', description: 'Map zoom used together with lat / lon.', control: { kind: 'range', min: 1, max: 20, step: 1, start: 10 } },
];

/** Every parameter, in URL order. */
export const ALL_PARAMS: DocParam[] = [
  ...ACCESS_PARAMS, ...UI_SWITCH_PARAMS, ...VEHICLE_PARAMS, ...SETTINGS_PARAMS, ...COLOR_PARAMS, ...MAP_PARAMS,
];

const PARAM_NAMES = new Set(ALL_PARAMS.map((param) => param.name));

/** Placeholder written into the URL while no key is entered. */
export const API_KEY_PLACEHOLDER = 'YOUR_API_KEY';

export type WidgetParamValues = Record<string, string>;

/** Name / value pairs of the URL, in URL order; api_key always first. */
export function widgetUrlParams(values: WidgetParamValues): { name: string, value: string }[] {
  const pairs = ALL_PARAMS
    .filter((param) => param.name !== 'api_key' && values[param.name] != null && values[param.name] !== '')
    .map((param) => ({ name: param.name, value: values[param.name] }));
  return [{ name: 'api_key', value: values['api_key'] || API_KEY_PLACEHOLDER }, ...pairs];
}

/** Commas stay readable (`chargers=ccs,type2`); everything else is encoded. */
export function encodeWidgetValue(value: string): string {
  return encodeURIComponent(value).replace(/%2C/gi, ',');
}

/** `base` is everything before the `?`: `https://widget.<domain>/` or `https://<domain>/widget`. */
export function buildWidgetUrl(base: string, values: WidgetParamValues): string {
  const query = widgetUrlParams(values).map(({ name, value }) => `${name}=${encodeWidgetValue(value)}`).join('&');
  return `${base}?${query}`;
}

/**
 * Reads the known parameters of a pasted widget URL (or just its query string). Names are
 * case-insensitive like in the widget; `lng` is taken as `lon`, and `#` is dropped from colors.
 */
export function parseWidgetUrl(input: string): WidgetParamValues | null {
  const text = input.trim();
  if (!text) return null;
  let search: string;
  try {
    search = new URL(text, 'https://widget.invalid/').search;
  } catch {
    return null;
  }
  if (!search && !text.includes('=')) return null;
  const params = new URLSearchParams(search || text.replace(/^[^?]*\?/, ''));
  const values: WidgetParamValues = {};
  params.forEach((value, rawName) => {
    let name = rawName.toLowerCase();
    if (name === 'lng') name = 'lon';
    if (!PARAM_NAMES.has(name) || value.trim() === '') return;
    values[name] = name.startsWith('color_') ? value.trim().replace(/^#/, '') : value.trim();
  });
  if (values['api_key'] === API_KEY_PLACEHOLDER) delete values['api_key'];
  return values;
}

export interface WidgetPreset {
  id: string;
  title: string;
  description: string;
  values: WidgetParamValues;
}

export const PRESETS: WidgetPreset[] = [
  {
    id: 'locked', title: 'Locked car demo',
    description: 'One specific car in range mode, with no vehicle, battery or settings controls.',
    values: { carid: '69', battery: '65', carselector: '0', batteryslider: '0', settings: '0', mode: 'range', lat: '47.4979', lon: '19.0402', zoom: '11' },
  },
  {
    id: 'dealer', title: 'Dealer app in German',
    description: 'Dark map, metric units, only CCS and Type 2 chargers, conservative reserves.',
    values: { carid: '69', lang: 'de', skin: 'dark', unit: 'metric', chargers: 'ccs,type2', reserveroute: '20', minbattery: '15' },
  },
  {
    id: 'brand', title: 'Brand colors',
    description: 'A green palette on the light skin; the map follows at the default tint.',
    values: { color_accent1: '2E7D32', color_accent2: '81C784', color_background: 'F4F8F4', color_text: '1B2E1C' },
  },
  {
    id: 'route', title: 'Route planner',
    description: 'Route mode, the user is free to change the car; no toll roads or ferries.',
    values: { mode: 'route', battery: '80', speedlimiter: '120', tollroads: '0', ferries: '0' },
  },
];
